import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { WhatsAppButton, ScrollToTopButton, SocialFloatingMenu } from "@/components/shared/FloatingButtons";
import { Chatbot } from "@/components/shared/Chatbot";
import { Toaster } from "sonner";
import { SOCIAL_LINKS } from "@/lib/site";

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["Organization", "EducationalOrganization"],
      "@id": "https://www.atmasammanschoolofai.com/#organization",
      name: "ASAI – Atmasamman School of AI",
      alternateName: "Atmasamman School of AI",
      slogan: "Learn Today. Lead Tomorrow.",
      url: "https://www.atmasammanschoolofai.com",
      logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_asai-core/artifacts/91eihqrm_ASAI%20LOGO.png",
      email: "info@atmasammanschoolofai.com",
      telephone: "+91-78300-60800",
      parentOrganization: {
        "@type": "Organization",
        name: "Atmasamman Group",
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: "Plot No. 98, First Floor, Above Reliance Smart Point, Opposite DAV Degree College, DAV Tiraha, Shivpuri",
        addressLocality: "Bulandshahr",
        addressRegion: "Uttar Pradesh",
        postalCode: "203001",
        addressCountry: "IN",
      },
      areaServed: {
        "@type": "AdministrativeArea",
        name: "India",
      },
      sameAs: [
        SOCIAL_LINKS.instagram,
        SOCIAL_LINKS.youtube,
        SOCIAL_LINKS.linkedin,
        SOCIAL_LINKS.facebook,
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://www.atmasammanschoolofai.com/#website",
      url: "https://www.atmasammanschoolofai.com",
      name: "ASAI – Atmasamman School of AI",
      publisher: { "@id": "https://www.atmasammanschoolofai.com/#organization" },
      inLanguage: "en-IN",
    },
  ],
};

export const Layout = () => {
  const { pathname } = useLocation();

  // Smooth momentum scrolling
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // Reset scroll on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Guard the tab title against external scripts overriding it (e.g. "Emergent | Fullstack App").
  // Per-page SEO titles set via useSeo are ASAI-branded, so we only reset when the title
  // is emptied or replaced with a non-ASAI value.
  useEffect(() => {
    const DEFAULT_TITLE = "ASAI – Atmasamman School of AI | Learn Today. Lead Tomorrow.";
    const isBad = (t) => !t || /emergent/i.test(t) || !/asai/i.test(t);
    if (isBad(document.title)) document.title = DEFAULT_TITLE;
    const observer = new MutationObserver(() => {
      if (isBad(document.title)) document.title = DEFAULT_TITLE;
    });
    const titleEl = document.querySelector("title");
    if (titleEl) observer.observe(titleEl, { childList: true });
    return () => observer.disconnect();
  }, []);

  // Inject Organization + WebSite JSON-LD (site-wide)
  useEffect(() => {
    const id = "asai-org-jsonld";
    let s = document.getElementById(id);
    if (!s) {
      s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(ORG_JSONLD);
      document.head.appendChild(s);
    }
    return () => { s?.remove(); };
  }, []);

  return (
    <div className="App flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
      <SocialFloatingMenu />
      <Chatbot />
      <ScrollToTopButton />
      <Toaster position="bottom-center" richColors />
    </div>
  );
};
